import { deleteSession, getSession } from "./api/client";
import { Stage0Onramp } from "./stages/Stage0Onramp";
import { Stage1Discovery } from "./stages/Stage1Discovery";
import { Stage2Drilldown } from "./stages/Stage2Drilldown";
import { Stage3Playback } from "./stages/Stage3Playback";
import { Stage4Blueprint } from "./stages/Stage4Blueprint";
import { StageWelcome } from "./stages/StageWelcome";
import type { Stage } from "./stages/Stage0Onramp";
import type { Session } from "./api/types";
import "./styles/app.css";

// ---- Theme toggle ----
const THEME_KEY = "swo_theme";

function applyTheme(theme: "dark" | "light"): void {
  if (theme === "light") {
    document.documentElement.setAttribute("data-theme", "light");
  } else {
    document.documentElement.removeAttribute("data-theme");
  }
  localStorage.setItem(THEME_KEY, theme);
}

function currentTheme(): "dark" | "light" {
  return document.documentElement.getAttribute("data-theme") === "light" ? "light" : "dark";
}

function initThemeToggle(): void {
  const btn = document.getElementById("theme-toggle");
  if (!btn) return;
  btn.addEventListener("click", () => {
    applyTheme(currentTheme() === "dark" ? "light" : "dark");
  });
}

initThemeToggle();

const appEl = document.getElementById("app")!;
let currentStage: Stage | null = null;

function mountStage(stage: Stage): void {
  currentStage?.unmount();
  appEl.innerHTML = "";
  currentStage = stage;
  stage.mount(appEl);
}

async function startOver(sessionId: string): Promise<void> {
  await deleteSession(sessionId);
  localStorage.removeItem("session_id");
  routeToStage(0, "");
}

function routeToStage(stageNumber: number, sessionId: string, existingSession: Session | null = null): void {
  // For live stage transitions the caller doesn't have the updated session — fetch it
  // resuming=false means: populate blueprint but start chat fresh (don't replay old history)
  if (!existingSession && sessionId && stageNumber > 0) {
    getSession(sessionId)
      .then((session) => _mountStage(stageNumber, sessionId, session, false))
      .catch(() => _mountStage(stageNumber, sessionId, null, false));
    return;
  }
  // existingSession was provided by init() on page reload — resume full history
  _mountStage(stageNumber, sessionId, existingSession, true);
}

function _mountStage(stageNumber: number, sessionId: string, existingSession: Session | null, resuming: boolean): void {
  const onStartOver = () => startOver(sessionId);
  switch (stageNumber) {
    case 0:
      mountStage(new Stage0Onramp((sid) => routeToStage(1, sid)));
      break;
    case 1:
      mountStage(new Stage1Discovery(sessionId, (next) => routeToStage(next, sessionId), existingSession, resuming, onStartOver));
      break;
    case 2:
      mountStage(new Stage2Drilldown(sessionId, (next) => routeToStage(next, sessionId), existingSession, resuming, onStartOver));
      break;
    case 3:
      mountStage(new Stage3Playback(sessionId, () => routeToStage(4, sessionId), onStartOver));
      break;
    case 4:
      mountStage(new Stage4Blueprint(sessionId, onStartOver));
      break;
    default:
      mountStage(new Stage0Onramp((sid) => routeToStage(1, sid)));
  }
}

async function init(): Promise<void> {
  if (window.location.pathname === '/welcome') {
    mountStage(new StageWelcome());
    return;
  }

  const sessionId = localStorage.getItem("session_id");

  if (sessionId) {
    try {
      const session = await getSession(sessionId);
      routeToStage(session.stage, sessionId, session);
      return;
    } catch {
      // Session expired or not found — clear it and start fresh
      localStorage.removeItem("session_id");
    }
  }

  // No valid session — show on-ramp
  mountStage(new Stage0Onramp((sid) => {
    localStorage.setItem("session_id", sid);
    routeToStage(1, sid);
  }));
}

init();
